// Cold-start language handling for the Inertia entrypoint.
//
// i18next boots without a catalog; the user's real language is a lazy, async
// import. If React renders before that import lands,
// the app paints one English frame and then re-renders in the user's language —
// the "flash of English" users see on app open. These helpers let the
// entrypoint hold the first paint until the right language is active.

// Which language the app should paint on cold start.
//
// An authenticated user's saved language wins over the server/URL locale (which
// is what an anonymous visitor on a localized URL like /cs/login gets), which
// wins over English. This mirrors the backend's set_locale priority in
// ApplicationController.
//
// The result is validated against the locales that actually ship a bundle
// (`availableLanguages`): a user still carrying a since-removed locale would
// otherwise trigger changeLanguage() into a resource-less language — text would
// fall back to English but Intl date/number formatting would follow the wrong
// locale. Unknown → English.
export function resolveBootLanguage(
  currentUser,
  propsLocale,
  availableLanguages,
) {
  const raw = currentUser?.language || propsLocale || 'en';
  return availableLanguages.has(raw) ? raw : 'en';
}

// Resolve once the target language is active — its bundle loaded and
// i18n.changeLanguage applied — OR once `timeoutMs` elapses, whichever is first.
//
// Every target, including English, is loaded before the first paint. The loader
// caches completed languages, so an already-loaded catalog still resolves
// immediately. Never rejects: a slow or failed bundle load releases the boot
// spinner at the deadline rather than hanging the app. A failed non-English
// bundle falls back to loading English without making English part of every
// successful locale's payload.
export function whenLanguageReady(
  lang,
  { i18n, loadLanguage, timeoutMs = 1500, onError } = {},
) {
  const applied = loadLanguage(lang)
    .then(() => {
      if (i18n.language !== lang) return i18n.changeLanguage(lang);
      return undefined;
    })
    .catch(async (error) => {
      // Swallow all failures — including a throwing error reporter — so this
      // promise can never reject and strand the caller's mount on the spinner.
      try {
        if (onError) onError(error);
      } catch {
        // reporting the load error must not itself break the boot
      }

      if (lang === 'en') return;
      try {
        await loadLanguage('en');
        if (i18n.language !== 'en') await i18n.changeLanguage('en');
      } catch (fallbackError) {
        try {
          if (onError) onError(fallbackError);
        } catch {
          // reporting the fallback error must not itself break the boot
        }
      }
    });

  const deadline = new Promise((resolve) => setTimeout(resolve, timeoutMs));
  return Promise.race([applied, deadline]);
}
